/**
 * connectorConfigsService
 *
 * Service layer for connector credential management.
 *
 * Scope model (after migration 406):
 *   workspace credential — user_id IS NULL; set by workspace admin
 *   user credential      — user_id = <uuid>; set by the individual user
 *
 * Resolution order for EntitySyncPanel / engine calls:
 *   1. User-scoped row (workspace_id + user_id + connector_type, is_active)
 *   2. Workspace-scoped row (workspace_id + connector_type, is_active)
 *   3. Neither → 'disabled'
 *
 * Three UI states:
 *   'disabled'   — no active row for this scope; tile hidden in EntitySyncPanel
 *   'available'  — active row exists but auth_data is empty; tile shows "Connect"
 *   'active'     — active row exists and auth_data has credentials; tile shows action buttons
 */

import { getSupabaseClient } from './supabaseClient';
import type { ConnectorType } from './connectors';

// ── Types ──────────────────────────────────────────────────────────────────────

export type ConnectorStatus = 'disabled' | 'available' | 'active';

export interface ConnectorConfigRow {
  id: string;
  workspaceId: string;
  userId: string | null;
  connectorType: ConnectorType;
  displayName: string | null;
  authData: Record<string, unknown>;
  isActive: boolean;
  lastSyncedAt: string | null;
  syncError: string | null;
  createdAt: string;
  updatedAt: string;
}

function rowToConfig(row: Record<string, unknown>): ConnectorConfigRow {
  return {
    id: row.id as string,
    workspaceId: row.workspace_id as string,
    userId: (row.user_id as string | null) ?? null,
    connectorType: row.connector_type as ConnectorType,
    displayName: (row.display_name as string | null) ?? null,
    authData: (row.auth_data as Record<string, unknown>) ?? {},
    isActive: (row.is_active as boolean) ?? false,
    lastSyncedAt: (row.last_synced_at as string | null) ?? null,
    syncError: (row.sync_error as string | null) ?? null,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

function deriveStatus(row: ConnectorConfigRow | null): ConnectorStatus {
  if (!row || !row.isActive) return 'disabled';
  const hasCredentials = Object.keys(row.authData).length > 0;
  return hasCredentials ? 'active' : 'available';
}

// ── Read helpers ───────────────────────────────────────────────────────────────

/**
 * Load all connector configs visible to the current user for a workspace.
 * Includes both workspace-level (user_id IS NULL) and the caller's personal rows.
 */
export async function listConnectorConfigs(
  workspaceId: string,
  userId?: string,
): Promise<{ data: ConnectorConfigRow[]; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    let query = supabase
      .from('connector_configs')
      .select('*')
      .eq('workspace_id', workspaceId);

    if (userId) {
      // Return workspace rows + this user's personal rows
      query = query.or(`user_id.is.null,user_id.eq.${userId}`);
    } else {
      query = query.is('user_id', null);
    }

    const { data, error } = await query.order('created_at', { ascending: true });
    if (error) return { data: [], error: error.message };
    return { data: (data ?? []).map(rowToConfig), error: null };
  } catch (err) {
    return { data: [], error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

/**
 * Get the resolved credential for a connector:
 * user row takes precedence over workspace row.
 */
export async function resolveConnectorConfig(
  workspaceId: string,
  userId: string | null,
  connectorType: ConnectorType,
): Promise<{ data: ConnectorConfigRow | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();

    // Fetch both workspace row and user row in one query
    const orClause = userId
      ? `user_id.is.null,user_id.eq.${userId}`
      : 'user_id.is.null';

    const { data, error } = await supabase
      .from('connector_configs')
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('connector_type', connectorType)
      .or(orClause);

    if (error) return { data: null, error: error.message };

    const rows = (data ?? []).map(rowToConfig);
    // Prefer user row over workspace row
    const userRow = rows.find((r) => r.userId === userId && r.isActive);
    const workspaceRow = rows.find((r) => r.userId === null && r.isActive);
    return { data: userRow ?? workspaceRow ?? null, error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

/**
 * Resolve the status of one connector for display in EntitySyncPanel.
 */
export async function getConnectorStatus(
  workspaceId: string,
  userId: string | null,
  connectorType: ConnectorType,
): Promise<ConnectorStatus> {
  const { data } = await resolveConnectorConfig(workspaceId, userId, connectorType);
  return deriveStatus(data);
}

/**
 * Resolve the status of multiple connectors in a single DB round-trip.
 */
export async function resolveConnectorStatuses(
  workspaceId: string,
  userId: string | null,
  connectorTypes: ConnectorType[],
): Promise<Record<ConnectorType, ConnectorStatus>> {
  const result = Object.fromEntries(
    connectorTypes.map((t) => [t, 'disabled' as ConnectorStatus]),
  ) as Record<ConnectorType, ConnectorStatus>;

  try {
    const supabase = getSupabaseClient();
    const orClause = userId ? `user_id.is.null,user_id.eq.${userId}` : 'user_id.is.null';

    const { data, error } = await supabase
      .from('connector_configs')
      .select('*')
      .eq('workspace_id', workspaceId)
      .in('connector_type', connectorTypes)
      .or(orClause);

    if (error || !data) return result;

    const rows = data.map(rowToConfig);

    for (const type of connectorTypes) {
      const userRow = rows.find((r) => r.connectorType === type && r.userId === userId && r.isActive);
      const wsRow = rows.find((r) => r.connectorType === type && r.userId === null && r.isActive);
      result[type] = deriveStatus(userRow ?? wsRow ?? null);
    }
  } catch {
    // silently return all-disabled on network error
  }

  return result;
}

// ── Write helpers ──────────────────────────────────────────────────────────────

/**
 * Enable a connector at workspace level without providing credentials yet.
 * Creates an is_active = true, auth_data = {} row — shows "Available" in EntitySyncPanel.
 * Used by workspace admins from the Connectors settings page.
 *
 * If a workspace row already has credentials, preserve them — never wipe auth_data
 * when toggling enable (OAuth / engine-written tokens must survive).
 */
export async function enableWorkspaceConnector(
  workspaceId: string,
  connectorType: ConnectorType,
  displayName?: string,
): Promise<{ data: ConnectorConfigRow | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data: existing } = await supabase
      .from('connector_configs')
      .select('auth_data')
      .eq('workspace_id', workspaceId)
      .eq('connector_type', connectorType)
      .is('user_id', null)
      .maybeSingle();

    const existingAuth =
      (existing as { auth_data?: Record<string, unknown> } | null)?.auth_data ?? {};
    const authData =
      existingAuth && Object.keys(existingAuth).length > 0 ? existingAuth : {};

    return upsertConnectorConfig(workspaceId, null, connectorType, authData, displayName);
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

/**
 * Save / update workspace-level credentials for a connector.
 * Sets is_active = true and writes the provided authData.
 */
export async function saveWorkspaceConnectorCredentials(
  workspaceId: string,
  connectorType: ConnectorType,
  authData: Record<string, unknown>,
  displayName?: string,
): Promise<{ data: ConnectorConfigRow | null; error: string | null }> {
  return upsertConnectorConfig(workspaceId, null, connectorType, authData, displayName);
}

/**
 * Save / update user-level credentials for a connector.
 * Creates a personal override that takes precedence over the workspace credential.
 */
export async function saveUserConnectorCredentials(
  workspaceId: string,
  userId: string,
  connectorType: ConnectorType,
  authData: Record<string, unknown>,
  displayName?: string,
): Promise<{ data: ConnectorConfigRow | null; error: string | null }> {
  return upsertConnectorConfig(workspaceId, userId, connectorType, authData, displayName);
}

/**
 * Disable a connector by setting is_active = false on the matching row.
 * Soft-disable — preserves the row for audit history.
 */
export async function disableConnector(
  workspaceId: string,
  userId: string | null,
  connectorType: ConnectorType,
): Promise<{ error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    let query = supabase
      .from('connector_configs')
      .update({ is_active: false, updated_at: new Date().toISOString() })
      .eq('workspace_id', workspaceId)
      .eq('connector_type', connectorType);

    if (userId === null) {
      query = query.is('user_id', null);
    } else {
      query = query.eq('user_id', userId);
    }

    const { error } = await query;
    return { error: error ? error.message : null };
  } catch (err) {
    return { error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

/**
 * Remove credentials from a connector row (clears auth_data → status becomes 'available').
 * Does NOT disable the connector — the tile stays visible with a "Connect" button.
 */
export async function clearConnectorCredentials(
  workspaceId: string,
  userId: string | null,
  connectorType: ConnectorType,
): Promise<{ error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    let query = supabase
      .from('connector_configs')
      .update({ auth_data: {}, updated_at: new Date().toISOString() })
      .eq('workspace_id', workspaceId)
      .eq('connector_type', connectorType);

    if (userId === null) {
      query = query.is('user_id', null);
    } else {
      query = query.eq('user_id', userId);
    }

    const { error } = await query;
    return { error: error ? error.message : null };
  } catch (err) {
    return { error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

// ── Internal upsert ────────────────────────────────────────────────────────────

async function upsertConnectorConfig(
  workspaceId: string,
  userId: string | null,
  connectorType: ConnectorType,
  authData: Record<string, unknown>,
  displayName?: string,
): Promise<{ data: ConnectorConfigRow | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();

    // Migration 406 uses partial unique indexes (WHERE user_id IS NULL / IS NOT NULL)
    // which PostgreSQL's ON CONFLICT (columns) cannot target directly.
    // Use a manual update-or-insert to avoid "no unique constraint" errors.
    const existingQuery = supabase
      .from('connector_configs')
      .select('id')
      .eq('workspace_id', workspaceId)
      .eq('connector_type', connectorType);

    const { data: existing } = userId === null
      ? await existingQuery.is('user_id', null).maybeSingle()
      : await existingQuery.eq('user_id', userId).maybeSingle();

    const payload = {
      workspace_id: workspaceId,
      user_id: userId,
      connector_type: connectorType,
      display_name: displayName ?? connectorType,
      auth_data: authData,
      is_active: true,
    };

    let data: unknown, error: { message: string } | null;

    if ((existing as { id?: string } | null)?.id) {
      ({ data, error } = await supabase
        .from('connector_configs')
        .update(payload)
        .eq('id', (existing as { id: string }).id)
        .select()
        .single());
    } else {
      ({ data, error } = await supabase
        .from('connector_configs')
        .insert(payload)
        .select()
        .single());
    }

    if (error) return { data: null, error: (error as { message: string }).message };
    return { data: rowToConfig(data as Record<string, unknown>), error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}
